/**
 * Tek AI sayfası — üç sekme: Belge · Şablon · Asistan.
 *
 * AI iki ayrı gezinme maddesine bölünmüştü: "AI Dosya Analiz" (v5) ve
 * "AI Danışman" (v6). Aynı işin iki kapısı, iki ayrı zihinsel model.
 *
 * BURADA JS YENİDEN YAZILMIYOR, DOM TAŞINIYOR.
 *
 * v5'in içeriği v6'nın içine bir sekme paneli olarak taşınır. Eleman
 * KİMLİKLERİ değişmez (`pdf-status`, `pdf-results`, …), dolayısıyla
 * `getElementById` ile çalışan mevcut kodun tamamı hiç dokunulmadan
 * çalışmaya devam eder. 3.300 satırlık çalışan bir AI katmanını yeniden
 * yazmak, "sistemi bozma" ölçütüyle bağdaşmazdı; taşımak bağdaşıyor.
 *
 * Asistan sekmesi v6'nın özgün içeriğidir ve hiç ellenmez — 3B görüntüleyici,
 * robot ve bölünmüş sohbet düzeni olduğu gibi kalır.
 *
 * KALDIRMAK İÇİN: bu klasörü sil, shell/index.ts listesinden satırı çıkar.
 * Sayfa kendiliğinden eski iki sekmeli hâline döner — HTML'de kalıcı bir
 * değişiklik yapılmadı.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';
import { TEXTS } from './texts.ts';
import {
  renderTemplateTab, learnFromReference, togglePick, saveCurrent, deleteTemplate,
  bestTemplate, applyTemplate, itemsFromPdf,
} from './template-ui.ts';

const TABS = ['asst', 'doc', 'tpl'] as const;
type Tab = typeof TABS[number];

/* Varsayılan sekme Assistant. Eskiden 'doc' idi: AI sayfası belge yükleme
   ekranıyla açılıyordu ve orb — sayfanın asıl yüzü — hiç görünmüyordu.
   Belge yükleme zaten girişin solundaki + ile bir tık uzakta. */
let active: Tab = 'asst';
/** v6'nın özgün içeriği — Asistan sekmesinde gösterilir. */
let assistantNodes: HTMLElement[] = [];
let installed = false;

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function t(k: string): string { return legacy.t(k); }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }

/* ── Kurulum: bir kez, DOM'u yeniden düzenler ──────────────────────── */

function install(): boolean {
  if (installed) return true;

  const tabbar = el('v6-tabbar');
  const v5 = el('v5');
  if (!tabbar || !v5) return false;

  const host = tabbar.parentElement;
  if (!host) return false;

  /* Sekme çubuğundan SONRAKİ her şey v6'nın özgün içeriğidir. */
  assistantNodes = Array.from(host.children)
    .filter((n): n is HTMLElement => n instanceof HTMLElement && n !== tabbar);

  /* v5'in gövdesini taşı. Kopyalamıyoruz — TAŞIYORUZ; iki kopya olsaydı
     `getElementById` ikisinden birini seçer ve hangisi olduğu belirsiz
     kalırdı. Taşımada kimlikler tek kalır. */
  const docPanel = document.createElement('div');
  docPanel.id = 'aip-doc';
  docPanel.style.cssText = 'flex:1;min-height:0;overflow-y:auto';
  while (v5.firstChild) docPanel.appendChild(v5.firstChild);
  host.appendChild(docPanel);

  const tplPanel = document.createElement('div');
  tplPanel.id = 'aip-tpl';
  tplPanel.style.cssText = 'flex:1;min-height:0;overflow-y:auto;display:none';
  host.appendChild(tplPanel);

  /* Eski gezinme maddesi gizlenir; artık tek kapı var. */
  const ni5 = el('ni5');
  if (ni5) ni5.style.display = 'none';

  renderTabs(tabbar);
  renderTemplate(tplPanel);
  installed = true;
  show('asst');
  return true;
}

function renderTabs(tabbar: HTMLElement): void {
  /* Kapatma düğmesi korunur: Asistan sekmesindeki robot görünümüne dönüş
     ona bağlı ve bu davranışı değiştirmiyoruz. */
  const closeBtn = tabbar.querySelector('button');
  tabbar.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;gap:2px;flex:1;padding:6px 0';

  for (const id of TABS) {
    const b = document.createElement('button');
    b.id = 'aip-tab-' + id;
    b.type = 'button';
    b.textContent = t('aip_' + id);
    b.style.cssText =
      'background:none;border:none;cursor:pointer;font-size:12px;font-weight:700;'
      + 'padding:7px 14px;border-radius:9px;color:var(--t2);transition:all .15s';
    b.addEventListener('click', () => show(id));
    wrap.appendChild(b);
  }

  tabbar.appendChild(wrap);
  if (closeBtn) tabbar.appendChild(closeBtn);
}

/* Şablon sekmesinin içeriği ayrı dosyada: motor pakette, arayüz burada.
   Bu dosya yalnızca sekmeleri yönetir, alan çıkarımını bilmez. */
function renderTemplate(host: HTMLElement): void {
  renderTemplateTab(host);
}

/* ── Sekme değiştirme ──────────────────────────────────────────────── */

/* Overlays among these nodes start hidden via inline display:none; forcing '' on reveal opened them empty. */
function concealNode(n: HTMLElement): void {
  if (n.dataset.aipHidden === '1') return;
  n.dataset.aipPrevDisplay = n.style.display;
  n.dataset.aipHidden = '1';
  n.style.display = 'none';
}

function revealNode(n: HTMLElement): void {
  if (n.dataset.aipHidden !== '1') return;
  n.style.display = n.dataset.aipPrevDisplay ?? '';
  delete n.dataset.aipHidden;
  delete n.dataset.aipPrevDisplay;
}

function show(tab: Tab): void {
  if (!installed) return;
  active = tab;

  const doc = el('aip-doc');
  const tpl = el('aip-tpl');
  if (doc) doc.style.display = tab === 'doc' ? '' : 'none';
  if (tpl) tpl.style.display = tab === 'tpl' ? '' : 'none';
  for (const n of assistantNodes) {
    if (tab === 'asst') revealNode(n);
    else concealNode(n);
  }

  for (const id of TABS) {
    const b = el('aip-tab-' + id);
    if (!b) continue;
    const on = id === tab;
    b.style.background = on ? 'var(--rA)' : 'none';
    b.style.color = on ? 'var(--red)' : 'var(--t2)';
  }
}

/**
 * v6 görünür olduğunda kurulumu garanti et.
 *
 * Kurulum DOM'a bağlı ve v6 geç oluşabiliyor; her açılışta tekrar denemek
 * `installed` bayrağı sayesinde bedava.
 */
function ensure(tab?: Tab): void {
  if (!install()) return;
  if (tab) show(tab);
}

export default defineFeature({
  id: 'ai-page',
  label: 'Tek AI sayfası',
  view: null,

  boot: () => {
    legacy.addTranslations(TEXTS);
    /* Açılışta DOM hazır olmayabilir; ilk gösterimde de deneniyor. */
    ensure();
  },

  legacyGlobals: {
    /** Eski `go('v5','ni5')` çağrıları buraya yönlenir. */
    aipOpenDoc: () => { legacy.navigate('v6', 'ni6'); ensure('doc'); },
    aipOpenTemplate: () => { legacy.navigate('v6', 'ni6'); ensure('tpl'); },
    aipOpenAssistant: () => { legacy.navigate('v6', 'ni6'); ensure('asst'); },
    /**
     * Asistandaki + düğmesi. Eskiden genel belge editörünü açıyordu (çeviri,
     * dilbilgisi, DOCX düzenleme). Artık dosya muhasebe okumasına gider:
     * Belge sekmesi açılır, seçili belge türüyle okunur, tabloda düzeltilir.
     */
    aipReadFinancial: (file: unknown) => {
      if (!(file instanceof File)) return;
      legacy.navigate('v6', 'ni6');
      ensure('doc');
      const g = globalThis as unknown as { pdfProcess?: (f: File) => void };
      if (typeof g.pdfProcess === 'function') g.pdfProcess(file);
    },
    /**
     * Kayıtlı bir şablon bu belgeye uyuyor mu? Uyuyorsa alanları döndür.
     *
     * Kullanıcı bu düzeni bize BİR KEZ gösterdi; genel çıkarımdan daha
     * güvenilir olmasının sebebi bu. Bu yüzden metin çıkarımından ÖNCE,
     * gömülü e-faturadan SONRA denenir: e-fatura belgenin kendi beyanıdır,
     * şablon kullanıcının öğrettiğidir, çıkarım ise tahminin en fazlası.
     *
     * Uyum eşiğin altındaysa null — yarısı tutmayan bir şablonu uygulamak
     * yanlış hücreleri doğru sanmak olurdu.
     */
    aipApplyTemplate: async (buf: unknown) => {
      if (!(buf instanceof ArrayBuffer)) return null;
      let items;
      try { items = await itemsFromPdf(buf); } catch { return null; }
      if (!items.length) return null;
      const hit = bestTemplate(items);
      if (!hit) return null;
      const g = globalThis as unknown as { exLocale?: () => unknown };
      const loc = g.exLocale ? g.exLocale() : null;
      if (!loc) return null;
      const fields = applyTemplate(items, hit.tpl, loc as never);
      return {
        name: hit.tpl.name,
        fit: hit.fit,
        fields: fields.map((f) => ({
          field: f.field,
          raw: f.raw,
          value: f.parsed.value,
          certain: f.parsed.certain,
          reason: f.parsed.reason ?? '',
          alternative: f.parsed.alternative ?? null,
        })),
      };
    },

    /* Şablon sekmesi — arayüz olayları. */
    aipLearnRef: (ev: unknown) => {
      const inp = (ev as { target?: HTMLInputElement } | null)?.target;
      const f = inp?.files && inp.files[0];
      if (f) void learnFromReference(f);
      if (inp) inp.value = '';
    },
    aipTogglePick: (i: unknown, on: unknown) => togglePick(Number(i), on === true),
    aipSaveTemplate: () => saveCurrent(),
    aipDeleteTemplate: (id: unknown) => {
      deleteTemplate(String(id));
      const host = el('aip-tpl');
      if (host) renderTemplateTab(host);
    },

    /** v6 her gösterildiğinde çağrılır. */
    aipEnsure: () => { ensure(); return active; },
    aipActiveTab: () => active,
  },
});
